import { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator'
import { getRole } from 'listable'
import { hasRoles } from 'acl'
import { types } from '../../module'
import { Button, Box, Card, CardContent, Grid, Typography, TextField, Checkbox, FormControlLabel, IconButton } from '@mui/material'
import { Alert, SingleSelectValidator, StyledModal } from 'frame/components'
import { isBornUser } from 'utils'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { themeV5 } from 'theme/v5'

const Form = ({
  error,
  data = {},
  disabled,
  fleets,
  roles,
  userRoles,
  onCancel,
  onDelete,
  onBlock,
  onSubmit,
  onChange,
  currentUser
}) => {
  const form = useRef()
  const topRef = useRef()
  const bottomRef = useRef()

  const [fleetIds, setFleetIds] = useState(data?.fleets || []) // for fleets checkbox state
  const [selectAll, setSelectAll] = useState(data?.fleets.length > 0 ? data?.fleets.length === fleetIds?.length : false)

  useEffect(() => { // fixes late fleet initializing issues
    data?.fleets && setFleetIds(data?.fleets)
  }, [data?.fleets]) // eslint-disable-line


  //for delete/user deactivation confirmation
  const [openDeactivationModal, setOpenDeactivationModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [showRequiredFleetsError, setShowRequiredFleetsError] = useState(false)

  // fleet is required except for SuperAdmin Role
  const isValidFleetSelction = (data.role && data.role.name === 'SuperAdmin') || fleetIds.length > 0

  const isSelected = fleetId => JSON.stringify(data.fleets).includes(fleetId)

  const onInputChange = ({ target: { id, name, value } }) =>
    onChange({ ...data, [id || name]: value })

  const onSelectAll = () => {
    const allFleets = fleets.map(node => ({ id: node.value, name: node.label }))
    setFleetIds(selectAll ? [] : allFleets)
    onChange({ ...data, fleets: selectAll ? [] : allFleets })
    setSelectAll(!selectAll)
  }

  const onSelectRoleChange = value => {
    let role = roles.filter(node => node.id === value)[0]
    onChange({ ...data, role })
  }

  const onSelect = (fleetId) => () => {
    if (isSelected(fleetId)) {
      const fleetRemoved = fleetIds && fleetIds.filter(node => node.id !== fleetId)
      setFleetIds(fleetRemoved)
      onChange({ ...data, fleets: fleetRemoved })
      return
    }

    let fleet = fleets && fleets.filter(node => node.value === fleetId)[0]
    let fleetToAdd = { id: fleet.value, name: fleet.label }
    setFleetIds(fleetIds.concat(fleetToAdd))
    onChange({ ...data, fleets: fleetIds.concat(fleetToAdd) })
  }

  const onSaveValidation = () => {
    if (form.current) {
      form.current.submit()
      !isValidFleetSelction && setShowRequiredFleetsError(true)
    }
  }

  const onSubmitForm = () => isValidFleetSelction &&
    onSubmit({
      ...data,
      nickname: data.nickname.trim(),
      fleets: fleetIds,
    })

  const roleOptions = roles && roles.map(node => ({
    label: getRole(node.name),
    value: node.id,
  }))

  const onDeactivateUser = () => {
    onBlock(`?userId=${data.id}&block=${!data.blocked}`)
    onCancel()
  }

  const onDeleteUser = () => {
    onDelete(data.id)
    onCancel()
  }

  useEffect(() => {
    fleetIds.length > 0 && setShowRequiredFleetsError(false)
  }, [fleetIds])

  useEffect(() => {
    ValidatorForm.addValidationRule('isValidEmail', (value) => {

      if (value === '') {
        return true
      }

      let domain = value.split(/@/)[1]
      let ext = domain && domain.split(/\./)[1]
      let isValidDomain = ext && ext.length > 1 // to avoid single character email extension
      let isEmailFormat = /^[^\s@]{2,}@[^\s@]{2,}\.[^\s@]{2,}$/.test(value)

      return isEmailFormat && isValidDomain
    })
    return () => {
      ValidatorForm.removeValidationRule('isValidEmail')
    }
  }, [data.email])

  const onScroll = (ref) => () => ref.current && ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return (
    <ValidatorForm autoComplete="off" noValidate ref={form} onSubmit={onSubmitForm}>
      <Card>
        <CardContent
          sx={{
            padding: { xs: '24px 9.6px 0 9.6px', sm: '24px' },
          }}>
          {error && (
            <Alert
              sx={{
                marginBottom: 4,
              }}>
              Something went wrong whilst trying to add this user.
            </Alert>
          )}

          <Typography gutterBottom variant="h4">
            Details
          </Typography>
          <Box
            sx={{
              margin: [2, 0],
            }}>
            <Grid container spacing={4}>
              <Grid item sm={6} xs={12}>
                <TextValidator
                  id="nickname"
                  disabled={disabled}
                  fullWidth
                  label="Name"
                  inputProps={{ maxLength: 50 }}
                  value={data.nickname || ''}
                  variant="outlined"
                  validators={['required']}
                  errorMessages={['This field is required']}
                  onChange={onInputChange} />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextValidator
                  id="email"
                  disabled={disabled}
                  fullWidth
                  label="Email"
                  value={data.email || ''}
                  variant="outlined"
                  validators={['required', 'isValidEmail']}
                  errorMessages={['This field is required', 'Must be a valid email']}
                  onChange={onInputChange} />
              </Grid>
            </Grid>
          </Box>

          {/* Fleet Selector Section */}
          <Grid
            sx={{
              marginTop: 4
            }}
            alignItems="flex-end"
            container
            justify="space-between"
            spacing={2}>
            <Grid item>
              <Typography gutterBottom variant="h4">
                Fleets
              </Typography>
              <Typography variant="body2">
              Please select the fleets you wish to view data for.
              </Typography>
            </Grid>
            <Grid item >
              <Button
                sx={{
                  minWidth: 180,
                  '@media (max-width:600px)': {
                    minWidth: '100%',
                  },
                }}
                id={selectAll ? 'unselect-btn' : 'select-btn'}
                variant="contained"
                onClick={() => onSelectAll()}>
                {selectAll ? 'Unselect All' : 'Select All'}
              </Button>
            </Grid>
          </Grid>

          <Box
            sx={{
              margin: [2, 0],
              maxHeight: 300,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}>
            <Grid container>
              <style>
                {` #checkbox-form-control > .MuiCheckbox-root { color: ${showRequiredFleetsError ? '#e53935' : '#546E7A'}; } `}
              </style>
              {fleets.map((node, index) => (
                <Grid key={index} item sm={6} xs={12}>

                  {index === 0 && <Box ref={topRef} />}

                  <FormControlLabel
                    id="checkbox-form-control"
                    control={
                      <Checkbox
                        checked={isSelected(node.value)}
                        onChange={onSelect(node.value)}
                        value={node.value}
                        color="default" />
                    }
                    label={node.label} />

                  {index === fleets.length - 1 && <Box ref={bottomRef} />}
                </Grid>
              )
              )}
            </Grid>
            {showRequiredFleetsError && (
              <Typography
                sx={{
                  color: themeV5.palette.error.main,
                  position: 'absolute',
                  zIndex: 99,
                }}>*Please select atleast 1 fleet
              </Typography>
            )}
          </Box>
          {/* End of Fleet Selector Section */}

          <Box
            sx={{
              marginTop: 4
            }}>
            <Typography gutterBottom variant="h4">
              Role
            </Typography>
            <Typography variant="body2">
              Please select the role this user will have for all the selected fleets.
            </Typography>
          </Box>

          <Box
            sx={{
              margin: [2, 0],
            }}>
            <Grid container spacing={4}>
              <Grid item sm={6} xs={12}>
                <SingleSelectValidator
                  id={`role`}
                  disabled={disabled}
                  label="Role"
                  options={roleOptions || []}
                  value={(data.role && data.role.id) || ''}
                  validators={['required']}
                  errorMessages={['This field is required']}
                  onChange={onSelectRoleChange} />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  id="status"
                  disabled={true}
                  fullWidth
                  label="Status"
                  value={data.blocked ? 'Blocked' : 'Active'}
                  variant="outlined" />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>


      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '@media (max-width:600px)': {
            display: 'block',
          },
        }}>
        <Button
          sx={{
            marginTop: 2,
            marginRight: 2,
            minWidth: 180,
            '@media (max-width:600px)': {
              minWidth: '100%',
            },
          }}
          color="primary"
          disabled={disabled}
          variant="contained"
          onClick={onSaveValidation}>
          Save Changes
        </Button>

        {hasRoles(['superadmin', 'fleetcontroller'], userRoles) && (
          <Button
            sx={{
              marginTop: 2,
              marginRight: 2,
              minWidth: 180,
              '@media (max-width:600px)': {
                minWidth: '100%',
              },
            }}
            disabled={disabled}
            variant="contained"
            onClick={() => setOpenDeactivationModal(true)}>
            {data.blocked ? 'Activate User' : 'Deactivate User'}
          </Button>
        )}

        {/* for born users only for easy maintenance */}
        {hasRoles(['superadmin'], userRoles) && isBornUser(currentUser.email)
          && !isBornUser(data.email) // prevent deleting main born accounts
          && (
            <Button
              sx={{
                marginTop: 2,
                marginRight: 2,
                minWidth: 180,
                '@media (max-width:600px)': {
                  minWidth: '100%',
                },
              }}
              disabled={disabled}
              variant="contained"
              onClick={() => setOpenDeleteModal(true)}>
              Delete User
            </Button>
          )}

        <Button
          sx={{
            marginTop: 2,
            marginRight: 2,
            minWidth: 180,
            '@media (max-width:600px)': {
              minWidth: '100%',
            },
          }}
          disabled={disabled} variant="contained" onClick={onCancel}>
          Cancel
        </Button>

        <Box
          sx={{
            marginLeft: 'auto',
            marginRight: 0,
            marginTop: 1.25,
          }}>
          <IconButton
            sx={{
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              },
            }}
            color="secondary" onClick={onScroll(topRef, 'top')}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              },
            }}
            color="secondary" onClick={onScroll(bottomRef, 'bottom')}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* for user deactivation */}
      <StyledModal
        open={openDeactivationModal}
        title={`Confirm User ${data.blocked ? 'Activation' : 'Deactivation'}`}
        onCancel={() => setOpenDeactivationModal(false)}
        onConfirm={onDeactivateUser}>
        {`Please confirm to ${data.blocked ? 'activate' : 'deactivate'} user ${data.nickname}.`}
      </StyledModal>

      {/* for delete */}
      <StyledModal
        open={openDeleteModal}
        title={`Confirm Delete User`}
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={onDeleteUser}>
        {`Please confirm to delete user ${data.nickname}.`}
      </StyledModal>
    </ValidatorForm>
  )
}

Form.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  data: PropTypes.oneOfType([
    PropTypes.object,
    types.ModelType,
  ]).isRequired,
  fleets: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onBlock: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentUser: PropTypes.object.isRequired,
}

export default Form
